export interface ServiceCategoryCsvRow {
  categoryId: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  description?: string;
}

export interface ServiceCategoryCsvError {
  line: number;
  message: string;
}

const SLUG = /^[a-z][a-z0-9_]{0,126}$/;

function parseCsvRecord(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

function parseBoolCell(raw: string, line: number): boolean {
  const s = raw.trim().toLowerCase();
  if (s === '') return true;
  if (['true', '1', 'yes'].includes(s)) return true;
  if (['false', '0', 'no'].includes(s)) return false;
  throw new Error(`isActive must be true/false or empty (line ${line})`);
}

/** RFC4180-style rows; header: categoryId,name,displayOrder[,isActive][,description] */
export function parseServiceCategoriesCsv(text: string): {
  items: ServiceCategoryCsvRow[];
  errors: ServiceCategoryCsvError[];
} {
  const errors: ServiceCategoryCsvError[] = [];
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim() !== '');
  if (lines.length < 2) {
    return {
      items: [],
      errors: [{line: 1, message: 'CSV needs a header row and at least one data row'}],
    };
  }

  const header = parseCsvRecord(lines[0]).map((h) => h.trim().toLowerCase());
  const col = (name: string) => header.indexOf(name);
  const iSlug = col('categoryid');
  const iName = col('name');
  const iOrder = col('displayorder');
  const iActive = col('isactive');
  const iDesc = col('description');

  if (iSlug < 0 || iName < 0 || iOrder < 0) {
    return {
      items: [],
      errors: [{line: 1, message: 'Header must include categoryId, name, displayOrder'}],
    };
  }

  const items: ServiceCategoryCsvRow[] = [];
  const seen = new Set<string>();

  for (let r = 1; r < lines.length; r++) {
    const lineNum = r + 1;
    try {
      const cells = parseCsvRecord(lines[r]);
      const categoryId = (cells[iSlug] ?? '').trim();
      if (categoryId === '') continue;

      if (!SLUG.test(categoryId)) {
        throw new Error(`invalid categoryId slug "${categoryId}"`);
      }
      if (seen.has(categoryId)) throw new Error(`duplicate categoryId "${categoryId}"`);
      seen.add(categoryId);

      const name = (cells[iName] ?? '').trim();
      if (!name) throw new Error('name is required');

      const orderRaw = (cells[iOrder] ?? '').trim();
      const displayOrder = parseInt(orderRaw, 10);
      if (!Number.isInteger(displayOrder)) throw new Error('displayOrder must be an integer');

      const activeRaw = iActive >= 0 ? (cells[iActive] ?? '') : '';
      const isActive = parseBoolCell(activeRaw, lineNum);

      const descRaw = iDesc >= 0 ? (cells[iDesc] ?? '').trim() : '';
      const description = descRaw === '' ? undefined : descRaw;

      items.push({categoryId, name, displayOrder, isActive, description});
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      errors.push({line: lineNum, message: msg});
    }
  }

  if (errors.length > 0) return {items: [], errors};
  if (items.length === 0) {
    return {items: [], errors: [{line: 1, message: 'No data rows found'}]};
  }
  return {items, errors: []};
}
